/**
 * useVulnerabilityFilters Hook
 *
 * Manages filter state for the vulnerabilities table including:
 * - Search text filtering
 * - Advanced filters (severity, status, source, etc.)
 * - Sort configuration
 * - URL parameter synchronization
 *
 * This hook encapsulates all filter-related state management logic
 * extracted from the main vulnerabilities component.
 */

import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { DateRangeFilter } from '@/components/filters/types';
import { Filter } from '@/hooks/useRisks';
import {
  createClearAllFiltersHandler,
  createDateRangeFilterHandler,
  createMultiSelectFilterHandler,
  createRangeFilterHandler,
  createResetAllFiltersHandler,
  getSelectedFilterValues,
} from '@/sections/vulnerabilities/utils/filterHelpers.util';
import { omit } from '@/utils/lodash.util';
import {
  collapseOriginValues,
  getConsolidatedOriginIds as getExpandedOrigins,
} from '@/utils/originDisplay.util';

/**
 * Hook parameters
 */
export interface UseVulnerabilityFiltersParams {
  /** Current filters state from useGetRisks */
  filters: Filter;
  /** Filter state setter from useGetRisks */
  setFilters: React.Dispatch<React.SetStateAction<Filter>>;
  /** Whether to sync filters with URL parameters */
  syncWithUrl?: boolean;
}

/**
 * Hook return value
 */
export interface UseVulnerabilityFiltersReturn {
  /** Current filter state */
  filters: Filter;
  /** Update filters state */
  setFilters: React.Dispatch<React.SetStateAction<Filter>>;
  /** Handler for severity filter changes */
  handleSeverityFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for status filter changes */
  handleStatusFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for origin filter changes */
  handleOriginFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for tag filter changes */
  handleTagFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for attack surface filter changes */
  handleAttackSurfaceFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for SSO filter changes */
  handleSSOFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for KEV filter changes */
  handleKEVFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for ticket filter changes */
  handleTicketFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for CVSS range filter changes */
  handleCVSSFilter: (value: { min: string; max: string } | undefined) => void;
  /** Handler for EPSS range filter changes */
  handleEPSSFilter: (value: { min: string; max: string } | undefined) => void;
  /** Handler for date range filter changes */
  handleDateRangeFilter: (value: DateRangeFilter | undefined) => void;
  /** Handler for last seen preset time window filter changes */
  handleLastSeenFilter: (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void;
  /** Handler for search text changes */
  handleSearchChange: (value: string) => void;
  /** Clears all advanced filters */
  clearAllFilters: () => void;
  /** Resets all filters to initial state */
  resetAllFilters: () => void;
  /** Gets selected values for a specific filter key */
  getSelectedValues: (filterKey: string) => string[];
}

/**
 * Hook for managing vulnerability filter state and URL synchronization.
 *
 * @param params - Hook configuration parameters
 * @returns Filter state and handler functions
 *
 * @example
 * ```tsx
 * const { data, filters, setFilters } = useGetRisks();
 * const filterHandlers = useVulnerabilityFilters({
 *   filters,
 *   setFilters,
 *   syncWithUrl: true
 * });
 *
 * // Use in component
 * <SearchBar
 *   value={filterHandlers.filters.search}
 *   onChange={filterHandlers.handleSearchChange}
 * />
 * <FilterDropdown
 *   selectedValues={filterHandlers.getSelectedValues('severity')}
 *   onApply={filterHandlers.handleSeverityFilter}
 * />
 * ```
 */
export function useVulnerabilityFilters({
  filters,
  setFilters,
  syncWithUrl = true,
}: UseVulnerabilityFiltersParams): UseVulnerabilityFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Apply URL parameters to filters on initial load
  // This effect only needs to run when URL params change, not when filters/setFilters change
  useEffect(() => {
    if (!syncWithUrl) return;

    const severityParam = searchParams.get('severity');
    const statusParam = searchParams.get('status');
    const attackSurfaceParam = searchParams.get('attackSurface');

    if (severityParam || statusParam || attackSurfaceParam) {
      const newAdvancedFilters = { ...filters.advancedFilters };

      if (severityParam) {
        newAdvancedFilters.severity = severityParam.split(',');
      }

      if (statusParam) {
        newAdvancedFilters.status = statusParam.split(',');
      }

      if (attackSurfaceParam) {
        newAdvancedFilters.attackSurface = attackSurfaceParam.split(',');
      }

      setFilters({
        ...filters,
        advancedFilters: newAdvancedFilters,
      });

      // Clear URL params after applying
      setSearchParams({});
    }
  }, [searchParams, syncWithUrl]);

  // Create filter handlers using utilities
  const handleSeverityFilter = useMemo(
    () => createMultiSelectFilterHandler('severity', setFilters),
    [setFilters]
  );

  const handleStatusFilter = useMemo(
    () => createMultiSelectFilterHandler('status', setFilters),
    [setFilters]
  );

  const handleOriginFilter = useMemo(
    () =>
      (selectedOptions: Array<{ value?: string | number; label?: string }>) => {
        setFilters(filters => {
          // Expand origin values to actual integration names
          const allOrigins: string[] = [];

          for (const option of selectedOptions) {
            const value = option.value?.toString() || '';
            const expanded = getExpandedOrigins(value);

            if (expanded) {
              allOrigins.push(...expanded);
            } else {
              allOrigins.push(value);
            }
          }

          return {
            ...filters,
            advancedFilters:
              allOrigins.length > 0
                ? {
                    ...filters.advancedFilters,
                    origin: allOrigins,
                  }
                : omit(filters.advancedFilters, 'origin'),
          };
        });
      },
    [setFilters]
  );

  const handleTagFilter = useMemo(
    () => createMultiSelectFilterHandler('tags', setFilters),
    [setFilters]
  );

  const handleAttackSurfaceFilter = useMemo(
    () => createMultiSelectFilterHandler('attackSurface', setFilters),
    [setFilters]
  );

  const handleSSOFilter = useMemo(
    () => createMultiSelectFilterHandler('sso', setFilters),
    [setFilters]
  );

  const handleKEVFilter = useMemo(
    () => createMultiSelectFilterHandler('kev', setFilters),
    [setFilters]
  );

  const handleTicketFilter = useMemo(
    () => createMultiSelectFilterHandler('ticket', setFilters),
    [setFilters]
  );

  const handleCVSSFilter = useMemo(
    () => createRangeFilterHandler('cvss', setFilters),
    [setFilters]
  );

  const handleEPSSFilter = useMemo(
    () => createRangeFilterHandler('epss', setFilters),
    [setFilters]
  );

  const handleDateRangeFilter = useMemo(
    () => createDateRangeFilterHandler(setFilters),
    [setFilters]
  );

  const handleLastSeenFilter = useMemo(
    () => createMultiSelectFilterHandler('lastSeen', setFilters),
    [setFilters]
  );

  const clearAllFilters = useMemo(
    () => createClearAllFiltersHandler(setFilters),
    [setFilters]
  );

  const resetAllFilters = useMemo(
    () => createResetAllFiltersHandler(setFilters),
    [setFilters]
  );

  const handleSearchChange = useMemo(
    () => (value: string) => {
      setFilters(prev => ({
        ...prev,
        search: value,
      }));
    },
    [setFilters]
  );

  const getSelectedValues = useMemo(
    () => (filterKey: string) => {
      const values = getSelectedFilterValues(
        filters.advancedFilters,
        filterKey
      );

      // For origin filter, collapse expanded values back to consolidated display values
      if (filterKey === 'origin') {
        return collapseOriginValues(values);
      }

      return values;
    },
    [filters.advancedFilters]
  );

  return {
    filters,
    setFilters,
    handleSeverityFilter,
    handleStatusFilter,
    handleOriginFilter,
    handleTagFilter,
    handleAttackSurfaceFilter,
    handleSSOFilter,
    handleKEVFilter,
    handleTicketFilter,
    handleCVSSFilter,
    handleEPSSFilter,
    handleDateRangeFilter,
    handleLastSeenFilter,
    handleSearchChange,
    clearAllFilters,
    resetAllFilters,
    getSelectedValues,
  };
}
